/* src/render/camera.ts — the orbit/pan/zoom rig (TASKS.md 1.5).
 *
 * A target-on-ground rig: the camera always looks at a point on the y=0 plane, described by
 * distance, pitch and yaw. That is the right model for a map — the ground never tilts out
 * from under you, and "where am I" is always one point rather than a free 6-DOF pose.
 *
 * Input map is PLAN.md Part 4 §5. Right-drag versus context menu is resolved by the shared
 * 6px/200ms threshold in src/ui/dispatch.ts; this file only claims a right-drag once it has
 * passed that distance, so a still right-click still reaches the menu.
 */

import type { PerspectiveCamera } from 'three';
import { Vector3 } from 'three';

export interface CameraLimits {
  readonly minDistance: number;
  readonly maxDistance: number;
  readonly minPitch: number;
  readonly maxPitch: number;
}

const LIMITS: CameraLimits = {
  minDistance: 60,
  // High enough to take in a whole metro area; the far LOD ring and the fog band are sized
  // to keep something real, then haze, under the camera all the way up.
  maxDistance: 15000,
  /*
   * Pitch is the camera's elevation above its ground target: high = top-down, low = looking
   * across toward the horizon. The floor is kept well above the horizon on purpose — tilt any
   * lower and the view fills with the empty sky and the hard line where loaded tiles stop.
   * Keeping the camera looking somewhat downward always keeps the city ahead of you, which is
   * the "there's something ahead, not just the void" the design wants. maxPitch stays short of
   * straight-down so the frustum-ground intersection that drives tile loading keeps converging.
   */
  minPitch: 0.5,
  maxPitch: 1.4,
};

const DRAG_THRESHOLD_PX = 6;

export class CameraRig {
  /** Look-at point on the ground plane, in local world metres. */
  readonly target = new Vector3(0, 0, 0);

  distance = 900;
  pitch = 0.85;
  yaw = 0.6;

  #dragButton: number | null = null;
  #lastX = 0;
  #lastY = 0;
  #downX = 0;
  #downY = 0;
  #passedThreshold = false;
  readonly #keys = new Set<string>();
  #detach: (() => void) | null = null;

  constructor(
    private readonly camera: PerspectiveCamera,
    private readonly element: HTMLElement,
  ) {}

  attach(): void {
    const onPointerDown = (e: PointerEvent): void => {
      if (e.button !== 0 && e.button !== 1 && e.button !== 2) return;
      this.#dragButton = e.button;
      this.#lastX = e.clientX;
      this.#lastY = e.clientY;
      this.#downX = e.clientX;
      this.#downY = e.clientY;
      this.#passedThreshold = false;
      this.element.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e: PointerEvent): void => {
      if (this.#dragButton === null) return;

      const dx = e.clientX - this.#lastX;
      const dy = e.clientY - this.#lastY;
      this.#lastX = e.clientX;
      this.#lastY = e.clientY;

      if (!this.#passedThreshold) {
        const moved = Math.hypot(e.clientX - this.#downX, e.clientY - this.#downY);
        if (moved <= DRAG_THRESHOLD_PX) return;
        this.#passedThreshold = true;
      }

      if (this.#dragButton === 0) this.orbit(dx, dy);
      else this.pan(dx, dy);
    };

    const onPointerUp = (e: PointerEvent): void => {
      this.#dragButton = null;
      if (this.element.hasPointerCapture(e.pointerId)) {
        this.element.releasePointerCapture(e.pointerId);
      }
    };

    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      // Exponential zoom: a wheel notch should cover the same *proportion* of the remaining
      // distance whether you are at 100 m or 10 km, or zooming out from street level takes
      // forever.
      this.zoom(Math.exp(e.deltaY * 0.0012));
    };

    const onKeyDown = (e: KeyboardEvent): void => {
      this.#keys.add(e.key.toLowerCase());
    };
    const onKeyUp = (e: KeyboardEvent): void => {
      this.#keys.delete(e.key.toLowerCase());
    };
    const onBlur = (): void => {
      this.#keys.clear();
      this.#dragButton = null;
    };

    this.element.addEventListener('pointerdown', onPointerDown);
    this.element.addEventListener('pointermove', onPointerMove);
    this.element.addEventListener('pointerup', onPointerUp);
    this.element.addEventListener('pointercancel', onPointerUp);
    this.element.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);

    this.#detach = () => {
      this.element.removeEventListener('pointerdown', onPointerDown);
      this.element.removeEventListener('pointermove', onPointerMove);
      this.element.removeEventListener('pointerup', onPointerUp);
      this.element.removeEventListener('pointercancel', onPointerUp);
      this.element.removeEventListener('wheel', onWheel);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }

  detach(): void {
    this.#detach?.();
    this.#detach = null;
  }

  orbit(dx: number, dy: number): void {
    this.yaw -= dx * 0.005;
    this.pitch = clamp(this.pitch - dy * 0.005, LIMITS.minPitch, LIMITS.maxPitch);
  }

  /** Pan across the ground plane. Speed scales with distance so it feels the same at any zoom. */
  pan(dx: number, dy: number): void {
    const speed = this.distance * 0.0016;
    const sin = Math.sin(this.yaw);
    const cos = Math.cos(this.yaw);
    this.target.x -= (dx * cos - dy * sin) * speed;
    this.target.z -= (dx * sin + dy * cos) * speed;
  }

  zoom(factor: number): void {
    this.distance = clamp(this.distance * factor, LIMITS.minDistance, LIMITS.maxDistance);
  }

  /** WASD / arrow nudge. `dt` in seconds, so movement is frame-rate independent. */
  update(dt: number): void {
    if (this.#keys.size > 0) {
      const speed = this.distance * 0.9 * dt;
      let forward = 0;
      let strafe = 0;
      if (this.#keys.has('w') || this.#keys.has('arrowup')) forward += 1;
      if (this.#keys.has('s') || this.#keys.has('arrowdown')) forward -= 1;
      if (this.#keys.has('a') || this.#keys.has('arrowleft')) strafe -= 1;
      if (this.#keys.has('d') || this.#keys.has('arrowright')) strafe += 1;

      if (forward !== 0 || strafe !== 0) {
        const [mx, mz] = groundMoveVector(forward, strafe, this.yaw);
        this.target.x += mx * speed;
        this.target.z += mz * speed;
      }
    }

    const cosPitch = Math.cos(this.pitch);
    this.camera.position.set(
      this.target.x + this.distance * cosPitch * Math.sin(this.yaw),
      this.target.y + this.distance * Math.sin(this.pitch),
      this.target.z + this.distance * cosPitch * Math.cos(this.yaw),
    );
    this.camera.lookAt(this.target);

    // Keep the depth range tight around what is actually visible. A fixed 1–30000 range at
    // street level wastes most of the depth buffer's precision on empty space and shows up
    // as z-fighting between roads and landuse.
    this.camera.near = Math.max(1, this.distance * 0.02);
    this.camera.far = Math.max(4000, this.distance * 12);
    this.camera.updateProjectionMatrix();
  }

  /** Shift the rig by a world-space delta. Used when the floating origin moves. */
  shift(dx: number, dz: number): void {
    this.target.x += dx;
    this.target.z += dz;
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Ground-plane movement vector for WASD nudging, in world (x, z) metres per unit input.
 *
 * The camera sits at `target + distance·(sinYaw, cosYaw)` on the ground and looks along
 * `−(sinYaw, cosYaw)` — so W (forward, +1) moves the target that way, and D (strafe, +1) moves
 * 90° clockwise from it, along `(cosYaw, −sinYaw)`. Exported and pure so the sign convention is
 * unit-tested: an earlier version flipped two of the four signs and inverted the controls at
 * every yaw except 0, which no camera-free assertion would have caught.
 */
export function groundMoveVector(
  forward: number,
  strafe: number,
  yaw: number,
): [number, number] {
  const sin = Math.sin(yaw);
  const cos = Math.cos(yaw);
  return [strafe * cos - forward * sin, -strafe * sin - forward * cos];
}
